/**
 * Utilidades de formateo de fechas (COMPARTIDO)
 *
 * Este módulo es compartido entre client y server.
 * Usa CommonJS exports que son compatibles con ambos:
 * - Node.js server (require directo)
 * - Client bundlers (webpack/vite transpilan CommonJS a ES6)
 *
 * Centraliza la lógica de formateo para evitar duplicación de código
 * y bugs de conversión de timezone
 */

/**
 * Formatea una fecha en formato ISO o timestamp a formato DD-MM-YYYY
 * Sin conversión de timezone - usa el valor literal de la fecha
 *
 * @param {string} dateString - Fecha en formato "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss"
 * @param {string} [fallback='-'] - Valor a retornar si la fecha es nula/vacía
 * @returns {string} Fecha formateada como "DD-MM-YYYY" o el fallback
 *
 * @example
 * formatDate('2024-06-10') // '10-06-2024'
 * formatDate('2024-06-10T00:00:00') // '10-06-2024'
 * formatDate(null) // '-'
 * formatDate(null, 'Sin fecha') // 'Sin fecha'
 */
const formatDate = (dateString, fallback = '-') => {
  if (!dateString) return fallback;

  // Extraer solo la parte de fecha, ignorando timestamp si existe
  const datePart = dateString.split('T')[0]; // "2024-06-10"

  // Validar formato básico YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    console.warn(`Formato de fecha inválido: ${dateString}`);
    return fallback;
  }

  const [year, month, day] = datePart.split('-');
  return `${day}-${month}-${year}`;
};

/**
 * Formatea una fecha a formato ISO (YYYY-MM-DD) desde formato DD-MM-YYYY o retorna ISO si ya está en ese formato
 * Útil para enviar datos al backend
 *
 * @param {string} dateString - Fecha en formato "DD-MM-YYYY" o "YYYY-MM-DD"
 * @returns {string|null} Fecha formateada como "YYYY-MM-DD" o null si es inválida
 *
 * @example
 * formatDateToISO('10-06-2024') // '2024-06-10'
 * formatDateToISO('2024-06-10') // '2024-06-10' (ya está en ISO)
 * formatDateToISO('invalid') // null
 */
const formatDateToISO = (dateString) => {
  if (!dateString || dateString === '-') return null;

  // Caso 1: Ya está en formato ISO (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString; // Retornar tal cual
  }

  // Caso 2: Formato DD-MM-YYYY (convertir a ISO)
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  }

  // Formato no reconocido
  console.warn(`Formato de fecha no reconocido: ${dateString}`);
  return null;
};

/**
 * Valida si una fecha en formato DD-MM-YYYY es válida
 *
 * @param {string} dateString - Fecha en formato "DD-MM-YYYY"
 * @returns {boolean} true si la fecha es válida
 */
const isValidDate = (dateString) => {
  if (!dateString || dateString === '-') return false;

  const iso = formatDateToISO(dateString);
  if (!iso) return false;

  const date = new Date(iso);
  return date instanceof Date && !isNaN(date);
};

/**
 * Formatea un timestamp completo a formato DD-MM-YYYY HH:mm
 * Sin conversión de timezone - usa el valor literal del timestamp
 *
 * @param {string} dateTimeString - Timestamp en formato ISO o similar
 * @param {string} [fallback='-'] - Valor a retornar si la fecha es nula/vacía
 * @returns {string} Fecha y hora formateadas como "DD-MM-YYYY HH:mm" o el fallback
 *
 * @example
 * formatDateTime('2024-06-10T14:30:00') // '10-06-2024 14:30'
 * formatDateTime('2024-06-10T14:30:00.000Z') // '10-06-2024 14:30'
 * formatDateTime(null) // '-'
 */
const formatDateTime = (dateTimeString, fallback = '-') => {
  if (!dateTimeString) return fallback;

  // Normalizar formato: convertir espacio a 'T' si es necesario
  // MySQL retorna "2025-10-08 08:27:52", necesitamos soportar también ISO "2025-10-08T08:27:52"
  const normalizedString = dateTimeString.includes('T')
    ? dateTimeString
    : dateTimeString.replace(' ', 'T');

  // Extraer fecha y hora del timestamp
  const parts = normalizedString.split('T');
  if (parts.length < 2) {
    console.warn(`Formato de timestamp inválido: ${dateTimeString}`);
    return fallback;
  }

  const datePart = parts[0]; // "2024-06-10"
  const timePart = parts[1].split('.')[0]; // "14:30:00" (sin milisegundos)

  // Validar formato básico YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    console.warn(`Formato de fecha inválido: ${dateTimeString}`);
    return fallback;
  }

  const [year, month, day] = datePart.split('-');
  const [hour, minute] = timePart.split(':');

  return `${day}-${month}-${year} ${hour}:${minute}`;
};

/**
 * Calcula la duración en días entre dos fechas (inclusive)
 *
 * Incluye ambos días (inicio y fin) en el cálculo.
 * Por ejemplo, del 1 al 3 = 3 días (1, 2, 3)
 *
 * @param {string} fechaInicio - Fecha de inicio en formato ISO (YYYY-MM-DD)
 * @param {string} fechaTermino - Fecha de término en formato ISO (YYYY-MM-DD)
 * @returns {number} Duración en días (0 si las fechas son inválidas)
 *
 * @example
 * calcularDuracionDias('2024-06-01', '2024-06-03') // 3 (incluye día 1, 2 y 3)
 * calcularDuracionDias('2024-06-01', '2024-06-01') // 1 (un solo día)
 * calcularDuracionDias(null, '2024-06-01') // 0 (fechas inválidas)
 */
const calcularDuracionDias = (fechaInicio, fechaTermino) => {
  if (!fechaInicio || !fechaTermino) return 0;

  const inicio = new Date(fechaInicio);
  const termino = new Date(fechaTermino);

  // Validar que son fechas válidas
  if (isNaN(inicio.getTime()) || isNaN(termino.getTime())) {
    return 0;
  }

  const diffTime = Math.abs(termino - inicio);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir ambos días

  return diffDays;
};

/**
 * Obtiene la fecha de hoy en UTC (formato YYYY-MM-DD)
 * Consistente entre desarrollo (Santiago) y producción (GCloud UTC)
 *
 * @returns {string} Fecha de hoy en formato ISO (YYYY-MM-DD)
 *
 * @example
 * getTodayUTC() // '2025-01-25' (en UTC)
 */
const getTodayUTC = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Obtiene la fecha de ayer en UTC (formato YYYY-MM-DD)
 * Consistente entre desarrollo (Santiago) y producción (GCloud UTC)
 *
 * @returns {string} Fecha de ayer en formato ISO (YYYY-MM-DD)
 *
 * @example
 * getYesterdayUTC() // '2025-01-24' (en UTC)
 */
const getYesterdayUTC = () => {
  return new Date(Date.now() - 86400000).toISOString().split('T')[0];
};

/**
 * Verifica si una fecha es editable (hoy o ayer en UTC)
 * Usado para validación de asistencias de equipos
 *
 * @param {string} fecha - Fecha en formato ISO (YYYY-MM-DD)
 * @returns {boolean} true si la fecha es hoy o ayer en UTC
 *
 * @example
 * esFechaEditableUTC('2025-01-25') // true si es hoy o ayer
 * esFechaEditableUTC('2025-01-20') // false (fecha anterior)
 */
const esFechaEditableUTC = (fecha) => {
  const hoyUTC = getTodayUTC();
  const ayerUTC = getYesterdayUTC();
  return fecha === hoyUTC || fecha === ayerUTC;
};

// CommonJS export (compatible con Node.js y bundlers modernos)
module.exports = {
  formatDate,
  formatDateToISO,
  formatDateTime,
  isValidDate,
  calcularDuracionDias,
  getTodayUTC,
  getYesterdayUTC,
  esFechaEditableUTC
};
